import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  Snackbar,
  Tooltip,
  Avatar,
  Card,
  CardContent,
  RadioGroup,
  Radio,
  FormHelperText,
  Stack,
  Divider,
  LinearProgress,
  AlertTitle,
  Backdrop,
  CircularProgress,
  Fade,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  LocalOffer as OfferIcon,
  Percent as PercentIcon,
  AttachMoney as MoneyIcon,
  DateRange as DateRangeIcon,
  RestaurantMenu as MenuIcon,
  ShoppingCart as CartIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Category as CategoryIcon,
  CardGiftcard as CardGiftcardIcon,
  Numbers as NumbersIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useSocket } from "../../context/SocketContext";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";

// Redux actions
import {
  fetchOffers,
  createOffer,
  updateOffer,
  deleteOffer,
  toggleOffer,
} from "../../redux/slices/offerSlice";
import { fetchMenu } from "../../redux/slices/menuSlice";
import { getCategories } from "../../redux/slices/categorySlice";

const Offers = () => {
  const dispatch = useDispatch();
  const { socket, isConnected } = useSocket();

  const { user } = useSelector((state) => state.user);
  const restaurantId = user?._id;

  // ========== REDUX STATES ==========
  const { offers, loading } = useSelector((state) => state.offers);
  const { list: menuItems, loading: menuLoading } = useSelector(
    (state) => state.menu,
  );
  const { categories, loading: categoriesLoading } = useSelector(
    (state) => state.category,
  );

  // ========== LOCAL STATES ==========
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [notification, setNotification] = useState(null);

  // ===== MUI ALERT DIALOGS =====
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: null,
    title: "",
  });

  const [statusDialog, setStatusDialog] = useState({
    open: false,
    id: null,
    currentStatus: false,
    title: "",
  });

  const [copyDialog, setCopyDialog] = useState({
    open: false,
    code: "",
  });

  // ===== MUI SNACKBAR =====
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // ===== MUI BACKDROP LOADING =====
  const [backdropOpen, setBackdropOpen] = useState(false);

  // ========== FORM STATE ==========
  const [formData, setFormData] = useState({
    restaurant: restaurantId,
    title: "",
    offerCategory: "DISCOUNT",
    scope: "CART",
    discountType: "PERCENT",
    value: "",
    getQuantity: 1,
    minQuantity: 1,
    minCartValue: "",
    maxDiscount: "",
    freeItem: "",
    categoryId: "",
    menuItem: "",
    expiryDate: null,
    totalUsageLimit: "",
    perUserLimit: 1,
    isActive: true,
  });

  // ============================================
  // ✅ SOCKET EVENT LISTENERS
  // ============================================
  useEffect(() => {
    if (!socket) return;

    // console.log("📡 Offers socket listeners setup");

    // ============================================
    // 1️⃣ NEW OFFER CREATED
    // ============================================
    socket.on('new-offer-created', (data) => {
      // console.log('🎉 New offer created in real-time:', data);
      
      // Refresh offers list
      dispatch(fetchOffers(restaurantId));
      
      setNotification({
        message: `🎉 New offer: ${data.offer.title} created!`,
        severity: "success",
      });
      setTimeout(() => setNotification(null), 5000);
    });

    // ============================================
    // 2️⃣ OFFER UPDATED
    // ============================================
    socket.on('offer-updated', (data) => {
      // console.log('📝 Offer updated in real-time:', data);
      
      // Refresh offers list
      dispatch(fetchOffers(restaurantId));
      
      setNotification({
        message: `📝 Offer updated: ${data.offer.title}`,
        severity: "info",
      });
      setTimeout(() => setNotification(null), 5000);
    });

    // ============================================
    // 3️⃣ OFFER DELETED
    // ============================================
    socket.on('offer-deleted', (data) => {
      // console.log('❌ Offer deleted in real-time:', data);
      
      // Refresh offers list
      dispatch(fetchOffers(restaurantId));
      
      setNotification({
        message: `❌ Offer deleted: ${data.offerTitle}`,
        severity: "warning",
      });
      setTimeout(() => setNotification(null), 5000);
    });

    // ============================================
    // 4️⃣ OFFER STATUS CHANGED
    // ============================================
    socket.on('offer-status-changed', (data) => {
      // console.log('🔄 Offer status changed in real-time:', data);
      
      // Refresh offers list
      dispatch(fetchOffers(restaurantId));
      
      setNotification({
        message: `🔄 Offer ${data.offer.isActive ? 'activated' : 'deactivated'}: ${data.offer.title}`,
        severity: data.offer.isActive ? "success" : "warning",
      });
      setTimeout(() => setNotification(null), 5000);
    });

    // ============================================
    // 5️⃣ NOTIFICATION EVENT
    // ============================================
    socket.on('new-notification', (data) => {
      // console.log('🔔 New notification:', data);
      setNotification({
        message: data.notification?.message || 'New notification',
        severity: "info",
      });
      setTimeout(() => setNotification(null), 5000);
    });

    return () => {
      socket.off('new-offer-created');
      socket.off('offer-updated');
      socket.off('offer-deleted');
      socket.off('offer-status-changed');
      socket.off('new-notification');
    };
  }, [socket, dispatch, restaurantId]);

  // ============================================
  // ✅ NOTIFICATION SOUND
  // ============================================
  const playNotificationSound = () => {
    try {
      const audio = new Audio('/sounds/notification.mp3');
      audio.volume = 0.3;
      audio.play().catch(e => console.log('Audio play failed'));
    } catch (error) {
      console.log('Audio not supported');
    }
  };

  useEffect(() => {
    if (notification && notification.severity === 'success') {
      playNotificationSound();
    }
  }, [notification]);

  // ========== FETCH DATA ON MOUNT ==========
  useEffect(() => {
    if (restaurantId) {
      dispatch(fetchOffers(restaurantId));
      dispatch(getCategories());
    }
  }, [dispatch, restaurantId]);

  useEffect(() => {
    dispatch(fetchMenu());
  }, [dispatch]);

  // ========== FILTERED MENU ITEMS ==========
  const availableMenuItems = menuItems?.filter(
    (item) => item.isAvailable === true,
  );

  // ========== HANDLE FORM CHANGE ==========
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ========== HANDLE SCOPE CHANGE ==========
  const handleScopeChange = (e) => {
    const scope = e.target.value;
    setFormData((prev) => ({
      ...prev,
      scope,
      menuItem: scope === "ITEM" ? prev.menuItem : "",
      categoryId: scope === "CATEGORY" ? prev.categoryId : "",
      minCartValue: scope === "CART" ? prev.minCartValue : "",
    }));
  };

  // ========== HANDLE OFFER CATEGORY CHANGE ==========
  const handleOfferCategoryChange = (e) => {
    const category = e.target.value;
    setFormData((prev) => ({
      ...prev,
      offerCategory: category,
      discountType: category === "DISCOUNT" ? "PERCENT" : "",
      value: "",
      getQuantity: category === "BOGO" ? 1 : 1,
      freeItem: category === "FREE_ITEM" ? "" : "",
      maxDiscount: category === "DISCOUNT" ? "" : "",
      menuItem: category === "DISCOUNT" && prev.scope === "ITEM" ? prev.menuItem : "",
    }));
  };

  // ========== HANDLE DATE CHANGE ==========
  const handleDateChange = (date) => {
    setFormData((prev) => ({
      ...prev,
      expiryDate: date,
    }));
  };

  // ========== RESET FORM ==========
  const resetForm = () => {
    setFormData({
      restaurant: restaurantId,
      title: "",
      offerCategory: "DISCOUNT",
      scope: "CART",
      discountType: "PERCENT",
      value: "",
      getQuantity: 1,
      minQuantity: 1,
      minCartValue: "",
      maxDiscount: "",
      freeItem: "",
      categoryId: "",
      menuItem: "",
      expiryDate: null,
      totalUsageLimit: "",
      perUserLimit: 1,
      isActive: true,
    });
    setEditMode(false);
    setSelectedOffer(null);
  };

  // ========== HANDLE EDIT ==========
  const handleEdit = (offer) => {
    setFormData({
      restaurant: offer.restaurant,
      title: offer.title,
      offerCategory: offer.offerCategory || "DISCOUNT",
      scope: offer.scope,
      discountType: offer.discountType || "PERCENT",
      value: offer.value || "",
      getQuantity: offer.getQuantity || 1,
      minQuantity: offer.minQuantity || 1,
      minCartValue: offer.minCartValue || "",
      maxDiscount: offer.maxDiscount || "",
      freeItem: offer.freeItem?._id || offer.freeItem || "",
      categoryId: offer.categoryId?._id || offer.categoryId || "",
      menuItem: offer.menuItem?._id || offer.menuItem || "",
      expiryDate: offer.expiryDate ? new Date(offer.expiryDate) : null,
      totalUsageLimit: offer.totalUsageLimit || "",
      perUserLimit: offer.perUserLimit || 1,
      isActive: offer.isActive,
    });
    setEditMode(true);
    setSelectedOffer(offer);
    setOpenDialog(true);
  };

  // ========== HANDLE SUBMIT ==========
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.title) {
      setSnackbar({
        open: true,
        message: "Please enter offer title",
        severity: "error",
      });
      return;
    }

    if (!formData.value || Number(formData.value) <= 0) {
      setSnackbar({
        open: true,
        message: "Please enter a valid value",
        severity: "error",
      });
      return;
    }

    if (formData.offerCategory === "DISCOUNT") {
      if (!formData.discountType) {
        setSnackbar({
          open: true,
          message: "Please select discount type",
          severity: "error",
        });
        return;
      }
      if (formData.discountType === "PERCENT" && Number(formData.value) > 100) {
        setSnackbar({
          open: true,
          message: "Percentage discount cannot exceed 100%",
          severity: "error",
        });
        return;
      }
    }

    if (formData.offerCategory === "FREE_ITEM" && !formData.freeItem) {
      setSnackbar({
        open: true,
        message: "Please select a free item",
        severity: "error",
      });
      return;
    }

    if (formData.scope === "ITEM" && formData.offerCategory === "DISCOUNT" && !formData.menuItem) {
      setSnackbar({
        open: true,
        message: "Please select a menu item",
        severity: "error",
      });
      return;
    }

    if (formData.scope === "CATEGORY" && !formData.categoryId) {
      setSnackbar({
        open: true,
        message: "Please select a category",
        severity: "error",
      });
      return;
    }

    setBackdropOpen(true);

    try {
      const offerData = {
        restaurant: restaurantId,
        title: formData.title,
        offerCategory: formData.offerCategory,
        scope: formData.scope,
        value: Number(formData.value),
        minQuantity: Number(formData.minQuantity) || 1,
        expiryDate: formData.expiryDate,
        isActive: formData.isActive,
      };

      if (formData.offerCategory === "DISCOUNT") {
        offerData.discountType = formData.discountType;
        if (formData.maxDiscount) {
          offerData.maxDiscount = Number(formData.maxDiscount);
        }
        if (formData.scope === "ITEM") {
          offerData.menuItem = formData.menuItem;
        }
        if (formData.scope === "CATEGORY") {
          offerData.categoryId = formData.categoryId;
        }
      }

      if (["BOGO", "FREE_ITEM"].includes(formData.offerCategory)) {
        offerData.getQuantity = Number(formData.getQuantity) || 1;
        if (formData.scope === "CATEGORY") {
          offerData.categoryId = formData.categoryId;
        }
      }

      if (formData.offerCategory === "FREE_ITEM") {
        offerData.freeItem = formData.freeItem;
      }

      if (formData.scope === "CART" && formData.minCartValue) {
        offerData.minCartValue = Number(formData.minCartValue);
      }

      if (formData.totalUsageLimit) {
        offerData.totalUsageLimit = Number(formData.totalUsageLimit);
      }
      if (formData.perUserLimit) {
        offerData.perUserLimit = Number(formData.perUserLimit);
      }

      if (editMode) {
        await dispatch(
          updateOffer({
            id: selectedOffer._id,
            formData: offerData,
          }),
        ).unwrap();
        setSnackbar({
          open: true,
          message: "Offer updated successfully!",
          severity: "success",
        });
      } else {
        await dispatch(createOffer(offerData)).unwrap();
        setSnackbar({
          open: true,
          message: "Offer created successfully!",
          severity: "success",
        });
      }
      setOpenDialog(false);
      resetForm();
    } catch (error) {
      console.error("Submit error:", error);
      setSnackbar({
        open: true,
        message: error || "Something went wrong",
        severity: "error",
      });
    } finally {
      setBackdropOpen(false);
    }
  };

  // ========== DELETE CONFIRM DIALOG ==========
  const handleDeleteClick = (id, title) => {
    setDeleteDialog({
      open: true,
      id,
      title,
    });
  };

  const handleDeleteConfirm = async () => {
    setDeleteDialog({ ...deleteDialog, open: false });
    setBackdropOpen(true);

    try {
      await dispatch(deleteOffer(deleteDialog.id)).unwrap();
      setSnackbar({
        open: true,
        message: `Offer "${deleteDialog.title}" deleted successfully!`,
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error || "Failed to delete offer",
        severity: "error",
      });
    } finally {
      setBackdropOpen(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, id: null, title: "" });
  };

  // ========== STATUS TOGGLE DIALOG ==========
  const handleToggleClick = (id, currentStatus, title) => {
    setStatusDialog({
      open: true,
      id,
      currentStatus,
      title,
    });
  };

  const handleToggleConfirm = async () => {
    setStatusDialog({ ...statusDialog, open: false });
    setBackdropOpen(true);

    try {
      await dispatch(toggleOffer(statusDialog.id)).unwrap();
      setSnackbar({
        open: true,
        message: `Offer "${statusDialog.title}" ${statusDialog.currentStatus ? "deactivated" : "activated"} successfully!`,
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error || "Failed to toggle offer status",
        severity: "error",
      });
    } finally {
      setBackdropOpen(false);
    }
  };

  const handleToggleCancel = () => {
    setStatusDialog({ open: false, id: null, currentStatus: false, title: "" });
  };

  // ========== COPY CODE DIALOG ==========
  const handleCopyClick = (offerCode) => {
    navigator.clipboard.writeText(offerCode);
    setCopyDialog({
      open: true,
      code: offerCode,
    });
  };

  const handleCopyClose = () => {
    setCopyDialog({ open: false, code: "" });
  };

  // ========== GET MENU ITEM NAME ==========
  const getMenuItemName = (offer) => {
    if (offer.scope === "CART") return "All Items";
    if (offer.scope === "CATEGORY") {
      return offer.categoryId?.name || "Category";
    }
    if (offer.menuItem) {
      if (typeof offer.menuItem === "object") {
        return offer.menuItem.itemName;
      }
      const item = menuItems?.find((i) => i._id === offer.menuItem);
      return item?.itemName || "Unknown Item";
    }
    return "Unknown";
  };

  // ========== GET OFFER TYPE LABEL ==========
  const getOfferTypeLabel = (offer) => {
    if (offer.offerCategory === "DISCOUNT") {
      return `${offer.value}${offer.discountType === "PERCENT" ? "%" : "₹"} OFF`;
    }
    if (offer.offerCategory === "BOGO") {
      return `Buy ${offer.value} Get ${offer.getQuantity || 1} Free`;
    }
    if (offer.offerCategory === "FREE_ITEM") {
      return "Free Item";
    }
    return "Offer";
  };

  // ========== GET OFFER TYPE ICON ==========
  const getOfferTypeIcon = (offer) => {
    if (offer.offerCategory === "DISCOUNT") {
      return offer.discountType === "PERCENT" ? <PercentIcon /> : <MoneyIcon />;
    }
    if (offer.offerCategory === "BOGO") {
      return <CartIcon />;
    }
    if (offer.offerCategory === "FREE_ITEM") {
      return <CardGiftcardIcon />;
    }
    return <OfferIcon />;
  };

  // ========== GET OFFER TYPE COLOR ==========
  const getOfferTypeColor = (offer) => {
    if (offer.offerCategory === "DISCOUNT") return "primary";
    if (offer.offerCategory === "BOGO") return "warning";
    if (offer.offerCategory === "FREE_ITEM") return "success";
    return "default";
  };

  // ========== GET USAGE INFO ==========
  const getUsageInfo = (offer) => {
    if (offer.totalUsageLimit) {
      return `${offer.usedCount || 0}/${offer.totalUsageLimit}`;
    }
    return `${offer.usedCount || 0} used`;
  };

  // ========== STATS CALCULATIONS ==========
  const activeOffers = offers?.filter((o) => o.isActive) || [];
  const cartOffers = offers?.filter((o) => o.scope === "CART") || [];
  const itemOffers = offers?.filter((o) => o.scope === "ITEM") || [];
  const categoryOffers = offers?.filter((o) => o.scope === "CATEGORY") || [];
  const bogoOffers = offers?.filter((o) => o.offerCategory === "BOGO") || [];

  // ========== RENDER ==========
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        // sx={{
        //   width: "100%",
        //   minHeight: "100vh",
        //   py: { xs: 1, md: 2 },
        // }}
        sx={{ mb: 4}}
      >
        <Box sx={{ maxWidth: 1350 }}>
          {/* ===== BACKDROP LOADING ===== */}
          <Backdrop
            sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={backdropOpen}
          >
            <CircularProgress color="inherit" />
            <Typography sx={{ ml: 2 }}>
              {editMode ? "Updating offer..." : "Creating offer..."}
            </Typography>
          </Backdrop>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 4,
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: "#1a365d" }}
              >
                Offers & Discounts
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body1" color="text.secondary">
                  Create and manage discounts, BOGO offers, and free items
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                resetForm();
                setOpenDialog(true);
              }}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1,
                bgcolor: "#2e7d32",
                "&:hover": { bgcolor: "#1b5e20" },
              }}
            >
              Create New Offer
            </Button>
          </Box>

          {/* ===== STATS CARDS ===== */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <Card
                sx={{
                  borderRadius: 3,
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                }}
              >
                <CardContent>
                  <Typography variant="h4">{offers?.length || 0}</Typography>
                  <Typography variant="body2">Total Offers</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <Card
                sx={{
                  borderRadius: 3,
                  background:
                    "linear-gradient(135deg, #4CAF50 0%, #8BC34A 100%)",
                  color: "white",
                }}
              >
                <CardContent>
                  <Typography variant="h4">{activeOffers.length}</Typography>
                  <Typography variant="body2">Active Offers</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <Card
                sx={{
                  borderRadius: 3,
                  background:
                    "linear-gradient(135deg, #FF9800 0%, #FF5722 100%)",
                  color: "white",
                }}
              >
                <CardContent>
                  <Typography variant="h4">{bogoOffers.length}</Typography>
                  <Typography variant="body2">BOGO Offers</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <Card
                sx={{
                  borderRadius: 3,
                  background:
                    "linear-gradient(135deg, #2196F3 0%, #03A9F4 100%)",
                  color: "white",
                }}
              >
                <CardContent>
                  <Typography variant="h4">{cartOffers.length}</Typography>
                  <Typography variant="body2">Cart Offers</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <Card
                sx={{
                  borderRadius: 3,
                  background:
                    "linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)",
                  color: "white",
                }}
              >
                <CardContent>
                  <Typography variant="h4">{categoryOffers.length}</Typography>
                  <Typography variant="body2">Category Offers</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* ===== OFFERS TABLE ===== */}
          <Paper
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: "#f8fafc" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Offer Title</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Scope</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Discount</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Item/Category</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Expiry</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Usage</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={9} sx={{ p: 4 }}>
                        <LinearProgress />
                      </TableCell>
                    </TableRow>
                  ) : offers?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} sx={{ p: 4, textAlign: "center" }}>
                        <OfferIcon
                          sx={{ fontSize: 60, color: "#e0e0e0", mb: 2 }}
                        />
                        <Typography variant="h6" color="text.secondary">
                          No offers found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Create your first offer to attract customers
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    offers?.map((offer, index) => (
                      <TableRow key={offer._id} hover>
                        <TableCell>#{index + 1}</TableCell>

                        {/* Offer Title with Code */}
                        <TableCell>
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600 }}
                            >
                              {offer.title}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                mt: 0.5,
                              }}
                            >
                              <Chip
                                label={
                                  offer.offerCategory === "DISCOUNT"
                                    ? "Discount"
                                    : offer.offerCategory === "BOGO"
                                      ? "BOGO"
                                      : "Free Item"
                                }
                                size="small"
                                sx={{
                                  fontSize: "0.65rem",
                                  height: 20,
                                  bgcolor:
                                    offer.offerCategory === "DISCOUNT"
                                      ? "#e3f2fd"
                                      : offer.offerCategory === "BOGO"
                                        ? "#fff3e0"
                                        : "#e8f5e9",
                                  color:
                                    offer.offerCategory === "DISCOUNT"
                                      ? "#1976d2"
                                      : offer.offerCategory === "BOGO"
                                        ? "#ed6c02"
                                        : "#2e7d32",
                                }}
                              />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Code:
                              </Typography>
                              <Chip
                                label={offer.offerCode}
                                size="small"
                                sx={{
                                  fontFamily: "monospace",
                                  fontSize: "0.7rem",
                                  height: 20,
                                  bgcolor: "#f5f5f5",
                                }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => handleCopyClick(offer.offerCode)}
                              >
                                <CopyIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        </TableCell>

                        {/* Scope */}
                        <TableCell>
                          <Chip
                            icon={
                              offer.scope === "CART" ? (
                                <CartIcon />
                              ) : offer.scope === "CATEGORY" ? (
                                <CategoryIcon />
                              ) : (
                                <MenuIcon />
                              )
                            }
                            label={
                              offer.scope === "CART"
                                ? "Cart"
                                : offer.scope === "CATEGORY"
                                  ? "Category"
                                  : "Item"
                            }
                            size="small"
                            sx={{
                              bgcolor:
                                offer.scope === "CART"
                                  ? "#e3f2fd"
                                  : offer.scope === "CATEGORY"
                                    ? "#f3e5f5"
                                    : "#fff3e0",
                              color:
                                offer.scope === "CART"
                                  ? "#1976d2"
                                  : offer.scope === "CATEGORY"
                                    ? "#9c27b0"
                                    : "#ed6c02",
                            }}
                          />
                        </TableCell>

                        {/* Discount/Offer */}
                        <TableCell>
                          <Chip
                            icon={getOfferTypeIcon(offer)}
                            label={getOfferTypeLabel(offer)}
                            color={getOfferTypeColor(offer)}
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                          {offer.offerCategory === "DISCOUNT" &&
                            offer.maxDiscount && (
                              <Typography
                                variant="caption"
                                display="block"
                                color="text.secondary"
                              >
                                Max: ₹{offer.maxDiscount}
                              </Typography>
                            )}
                        </TableCell>

                        {/* Item/Category */}
                        <TableCell>
                          {offer.scope === "CART" ? (
                            <Box>
                              <Typography variant="body2">All Items</Typography>
                              {offer.minCartValue > 0 && (
                                <Typography variant="caption" color="text.secondary">
                                  Min Cart: ₹{offer.minCartValue}
                                </Typography>
                              )}
                            </Box>
                          ) : offer.scope === "CATEGORY" ? (
                            <Box display="flex" alignItems="center" gap={1}>
                              <Avatar
                                src={offer.categoryId?.image}
                                sx={{ width: 24, height: 24 }}
                              >
                                {offer.categoryId?.name?.charAt(0)}
                              </Avatar>
                              <Typography variant="body2">
                                {offer.categoryId?.name || "Category"}
                              </Typography>
                            </Box>
                          ) : offer.offerCategory === "FREE_ITEM" ? (
                            <Box display="flex" alignItems="center" gap={1}>
                              <Avatar
                                src={offer.freeItem?.image}
                                sx={{ width: 24, height: 24 }}
                              >
                                {offer.freeItem?.itemName?.charAt(0)}
                              </Avatar>
                              <Typography variant="body2">
                                {offer.freeItem?.itemName || "Free Item"}
                              </Typography>
                            </Box>
                          ) : (
                            <Box display="flex" alignItems="center" gap={1}>
                              <Avatar
                                src={offer.menuItem?.image}
                                sx={{ width: 24, height: 24 }}
                              >
                                {offer.menuItem?.itemName?.charAt(0)}
                              </Avatar>
                              <Typography variant="body2">
                                {getMenuItemName(offer)}
                              </Typography>
                            </Box>
                          )}
                        </TableCell>

                        {/* Expiry */}
                        <TableCell>
                          {offer.expiryDate ? (
                            <Typography variant="caption">
                              {format(new Date(offer.expiryDate), "dd/MM/yyyy")}
                            </Typography>
                          ) : (
                            <Chip
                              label="No expiry"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </TableCell>

                        {/* Usage */}
                        <TableCell>
                          <Tooltip title={`Used ${offer.usedCount || 0} times`}>
                            <Chip
                              icon={<NumbersIcon />}
                              label={getUsageInfo(offer)}
                              size="small"
                              variant="outlined"
                              sx={{
                                bgcolor: offer.usedCount > 0 ? "#fff3e0" : "#f5f5f5",
                              }}
                            />
                          </Tooltip>
                        </TableCell>

                        {/* Status */}
                        <TableCell>
                          <Chip
                            label={offer.isActive ? "Active" : "Inactive"}
                            color={offer.isActive ? "success" : "default"}
                            size="small"
                          />
                        </TableCell>

                        {/* Actions */}
                        <TableCell align="center">
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="center"
                          >
                            <Tooltip title="Toggle Status">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleToggleClick(
                                    offer._id,
                                    offer.isActive,
                                    offer.title,
                                  )
                                }
                                color={offer.isActive ? "warning" : "success"}
                              >
                                {offer.isActive ? (
                                  <ToggleOnIcon fontSize="small" />
                                ) : (
                                  <ToggleOffIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(offer)}
                                color="primary"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleDeleteClick(offer._id, offer.title)
                                }
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* ===== CREATE/EDIT OFFER DIALOG ===== */}
          <Dialog
            open={openDialog}
            onClose={() => {
              setOpenDialog(false);
              resetForm();
            }}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}
          >
            <DialogTitle
              sx={{ borderBottom: "1px solid", borderColor: "divider" }}
            >
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar
                  sx={{ bgcolor: editMode ? "warning.main" : "success.main" }}
                >
                  {editMode ? <EditIcon /> : <AddIcon />}
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {editMode ? "Edit Offer" : "Create New Offer"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {editMode
                      ? "Update your discount offer"
                      : "Add discounts, BOGO offers, or free items"}
                  </Typography>
                </Box>
              </Box>
            </DialogTitle>

            <DialogContent sx={{ pt: 3 }}>
              <Grid container spacing={2} mt={3}>
                {/* ===== OFFER TITLE ===== */}
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Offer Title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="e.g., Summer Special, 3 Pizza Get 1 Free, Buy Pizza Get Free Drink"
                    required
                  />
                </Grid>

                {/* ===== OFFER CATEGORY SELECTION ===== */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Offer Category *
                  </Typography>
                  <RadioGroup
                    row
                    name="offerCategory"
                    value={formData.offerCategory}
                    onChange={handleOfferCategoryChange}
                  >
                    <FormControlLabel
                      value="DISCOUNT"
                      control={<Radio />}
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <PercentIcon fontSize="small" />
                          <Typography>Discount (% or ₹)</Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="BOGO"
                      control={<Radio />}
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <CartIcon fontSize="small" />
                          <Typography>Buy X Get Y Free</Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="FREE_ITEM"
                      control={<Radio />}
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <CardGiftcardIcon fontSize="small" />
                          <Typography>Free Item with Purchase</Typography>
                        </Box>
                      }
                    />
                  </RadioGroup>
                </Grid>

                {/* ===== SCOPE SELECTION ===== */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Apply Offer On *
                  </Typography>
                  <RadioGroup
                    row
                    name="scope"
                    value={formData.scope}
                    onChange={handleScopeChange}
                  >
                    <FormControlLabel
                      value="CART"
                      control={<Radio />}
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <CartIcon fontSize="small" />
                          <Typography>Entire Cart</Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="CATEGORY"
                      control={<Radio />}
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <CategoryIcon fontSize="small" />
                          <Typography>Category</Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="ITEM"
                      control={<Radio />}
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <MenuIcon fontSize="small" />
                          <Typography>Specific Item</Typography>
                        </Box>
                      }
                    />
                  </RadioGroup>
                </Grid>

                {/* ===== CATEGORY DROPDOWN ===== */}
                {formData.scope === "CATEGORY" && (
                  <Grid size={{ xs: 12 }}>
                    <FormControl fullWidth required>
                      <InputLabel>Select Category</InputLabel>
                      <Select
                        name="categoryId"
                        value={formData.categoryId}
                        onChange={handleInputChange}
                        label="Select Category *"
                      >
                        {categoriesLoading ? (
                          <MenuItem disabled>Loading categories...</MenuItem>
                        ) : categories?.length === 0 ? (
                          <MenuItem disabled>No categories available</MenuItem>
                        ) : (
                          categories?.map((category) => (
                            <MenuItem key={category._id} value={category._id}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Avatar
                                  src={category.image}
                                  sx={{ width: 28, height: 28 }}
                                >
                                  {category.name?.charAt(0)}
                                </Avatar>
                                <Typography variant="body2">
                                  {category.name}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))
                        )}
                      </Select>
                      <FormHelperText>
                        Select the category you want to apply this offer to
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                )}

                {/* ===== MENU ITEM DROPDOWN (for ITEM scope) ===== */}
                {formData.scope === "ITEM" &&
                  formData.offerCategory === "DISCOUNT" && (
                    <Grid size={{ xs: 12 }}>
                      <FormControl fullWidth required>
                        <InputLabel>Select Menu Item</InputLabel>
                        <Select
                          name="menuItem"
                          value={formData.menuItem}
                          onChange={handleInputChange}
                          label="Select Menu Item *"
                        >
                          {menuLoading ? (
                            <MenuItem disabled>Loading menu items...</MenuItem>
                          ) : availableMenuItems.length === 0 ? (
                            <MenuItem disabled>
                              No menu items available
                            </MenuItem>
                          ) : (
                            availableMenuItems.map((item) => (
                              <MenuItem key={item._id} value={item._id}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Avatar
                                    src={item.image}
                                    sx={{ width: 28, height: 28 }}
                                  >
                                    {item.itemName?.charAt(0)}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="body2">
                                      {item.itemName}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      ₹{item.basePrice || item.price}
                                    </Typography>
                                  </Box>
                                </Box>
                              </MenuItem>
                            ))
                          )}
                        </Select>
                        <FormHelperText>
                          Select the menu item you want to apply this offer to
                        </FormHelperText>
                      </FormControl>
                    </Grid>
                  )}

                {/* ===== BOGO/FREE_ITEM FIELDS ===== */}
                {["BOGO", "FREE_ITEM"].includes(formData.offerCategory) && (
                  <>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        label="Buy Quantity *"
                        name="value"
                        type="number"
                        value={formData.value}
                        onChange={handleInputChange}
                        required
                        helperText="Number of items customer needs to buy"
                        InputProps={{ inputProps: { min: 1 } }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        label="Get Quantity *"
                        name="getQuantity"
                        type="number"
                        value={formData.getQuantity}
                        onChange={handleInputChange}
                        required
                        helperText="Number of free items customer gets"
                        InputProps={{ inputProps: { min: 1 } }}
                      />
                    </Grid>
                  </>
                )}

                {/* ===== FREE ITEM SELECTION ===== */}
                {formData.offerCategory === "FREE_ITEM" && (
                  <Grid size={{ xs: 12 }}>
                    <FormControl fullWidth required>
                      <InputLabel>Select Free Item</InputLabel>
                      <Select
                        name="freeItem"
                        value={formData.freeItem}
                        onChange={handleInputChange}
                        label="Select Free Item *"
                      >
                        {menuLoading ? (
                          <MenuItem disabled>Loading menu items...</MenuItem>
                        ) : availableMenuItems.length === 0 ? (
                          <MenuItem disabled>No menu items available</MenuItem>
                        ) : (
                          availableMenuItems.map((item) => (
                            <MenuItem key={item._id} value={item._id}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Avatar
                                  src={item.image}
                                  sx={{ width: 28, height: 28 }}
                                >
                                  {item.itemName?.charAt(0)}
                                </Avatar>
                                <Box>
                                  <Typography variant="body2">
                                    {item.itemName}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    ₹{item.basePrice || item.price}
                                  </Typography>
                                </Box>
                              </Box>
                            </MenuItem>
                          ))
                        )}
                      </Select>
                      <FormHelperText>
                        Select the item that will be given for free
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                )}

                {/* ===== DISCOUNT TYPE FIELDS ===== */}
                {formData.offerCategory === "DISCOUNT" && (
                  <>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Discount Type *
                      </Typography>
                      <RadioGroup
                        row
                        name="discountType"
                        value={formData.discountType}
                        onChange={handleInputChange}
                      >
                        <FormControlLabel
                          value="PERCENT"
                          control={<Radio />}
                          label={
                            <Box display="flex" alignItems="center" gap={1}>
                              <PercentIcon fontSize="small" />
                              <Typography>Percentage (%)</Typography>
                            </Box>
                          }
                        />
                        <FormControlLabel
                          value="FLAT"
                          control={<Radio />}
                          label={
                            <Box display="flex" alignItems="center" gap={1}>
                              <MoneyIcon fontSize="small" />
                              <Typography>Flat Amount (₹)</Typography>
                            </Box>
                          }
                        />
                      </RadioGroup>
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        label={`Discount ${formData.discountType === "PERCENT" ? "%" : "₹"}`}
                        name="value"
                        type="number"
                        value={formData.value}
                        onChange={handleInputChange}
                        required
                        InputProps={{
                          startAdornment: (
                            <Box component="span" sx={{ mr: 1 }}>
                              {formData.discountType === "PERCENT" ? "%" : "₹"}
                            </Box>
                          ),
                        }}
                        helperText={
                          formData.discountType === "PERCENT"
                            ? "Enter percentage (max 100%)"
                            : "Enter amount in rupees"
                        }
                      />
                    </Grid>

                    {formData.discountType === "PERCENT" && (
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          label="Maximum Discount (₹)"
                          name="maxDiscount"
                          type="number"
                          value={formData.maxDiscount}
                          onChange={handleInputChange}
                          helperText="Leave empty for no maximum limit"
                        />
                      </Grid>
                    )}
                  </>
                )}

                {/* ===== MINIMUM QUANTITY ===== */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Minimum Quantity"
                    name="minQuantity"
                    type="number"
                    value={formData.minQuantity}
                    onChange={handleInputChange}
                    helperText="Minimum items required to apply offer"
                    InputProps={{ inputProps: { min: 1 } }}
                  />
                </Grid>

                {/* ===== MINIMUM CART VALUE (for CART scope) ===== */}
                {formData.scope === "CART" && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Minimum Cart Value (₹)"
                      name="minCartValue"
                      type="number"
                      value={formData.minCartValue}
                      onChange={handleInputChange}
                      helperText="Minimum order value required"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                  </Grid>
                )}

                {/* ===== EXPIRY DATE ===== */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <DatePicker
                    label="Expiry Date (Optional)"
                    value={formData.expiryDate}
                    onChange={handleDateChange}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        helperText: "Leave empty for no expiry",
                      },
                    }}
                  />
                </Grid>

                {/* ===== USAGE LIMITS ===== */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Total Usage Limit"
                    name="totalUsageLimit"
                    type="number"
                    value={formData.totalUsageLimit}
                    onChange={handleInputChange}
                    helperText="Maximum times this offer can be used"
                    InputProps={{ inputProps: { min: 1 } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Per User Limit"
                    name="perUserLimit"
                    type="number"
                    value={formData.perUserLimit}
                    onChange={handleInputChange}
                    helperText="How many times per customer"
                    InputProps={{ inputProps: { min: 1 } }}
                  />
                </Grid>

                {/* ===== STATUS TOGGLE ===== */}
                <Grid
                  size={{ xs: 12, md: 6 }}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isActive}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            isActive: e.target.checked,
                          }))
                        }
                      />
                    }
                    label="Activate offer immediately"
                  />
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions
              sx={{ p: 3, borderTop: "1px solid", borderColor: "divider" }}
            >
              <Button
                onClick={() => {
                  setOpenDialog(false);
                  resetForm();
                }}
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                variant="contained"
                disabled={loading}
                sx={{
                  bgcolor: "#2e7d32",
                  "&:hover": { bgcolor: "#1b5e20" },
                }}
              >
                {loading
                  ? "Saving..."
                  : editMode
                    ? "Update Offer"
                    : "Create Offer"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* ===== DELETE CONFIRMATION DIALOG ===== */}
          <Dialog
            open={deleteDialog.open}
            onClose={handleDeleteCancel}
            PaperProps={{ sx: { borderRadius: 3, maxWidth: 400 } }}
          >
            <DialogTitle sx={{ pb: 1 }}>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: "error.main" }}>
                  <DeleteIcon />
                </Avatar>
                <Typography variant="h6">Delete Offer</Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                <AlertTitle>Warning</AlertTitle>
                This action cannot be undone.
              </Alert>
              <DialogContentText>
                Are you sure you want to delete{" "}
                <strong>"{deleteDialog.title}"</strong>?
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button onClick={handleDeleteCancel} variant="outlined">
                Cancel
              </Button>
              <Button
                onClick={handleDeleteConfirm}
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
              >
                Delete Offer
              </Button>
            </DialogActions>
          </Dialog>

          {/* ===== STATUS TOGGLE DIALOG ===== */}
          <Dialog
            open={statusDialog.open}
            onClose={handleToggleCancel}
            PaperProps={{ sx: { borderRadius: 3, maxWidth: 400 } }}
          >
            <DialogTitle sx={{ pb: 1 }}>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar
                  sx={{
                    bgcolor: statusDialog.currentStatus
                      ? "warning.main"
                      : "success.main",
                  }}
                >
                  {statusDialog.currentStatus ? (
                    <ToggleOffIcon />
                  ) : (
                    <ToggleOnIcon />
                  )}
                </Avatar>
                <Typography variant="h6">
                  {statusDialog.currentStatus
                    ? "Deactivate Offer"
                    : "Activate Offer"}
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Alert
                severity={statusDialog.currentStatus ? "warning" : "info"}
                sx={{ mb: 2, borderRadius: 2 }}
              >
                <AlertTitle>
                  {statusDialog.currentStatus ? "Deactivate" : "Activate"}
                </AlertTitle>
                {statusDialog.currentStatus
                  ? "This offer will no longer be available to customers"
                  : "This offer will be visible and available to customers"}
              </Alert>
              <DialogContentText>
                Are you sure you want to{" "}
                {statusDialog.currentStatus ? "deactivate" : "activate"}{" "}
                <strong>"{statusDialog.title}"</strong>?
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button onClick={handleToggleCancel} variant="outlined">
                Cancel
              </Button>
              <Button
                onClick={handleToggleConfirm}
                variant="contained"
                color={statusDialog.currentStatus ? "warning" : "success"}
              >
                {statusDialog.currentStatus ? "Deactivate" : "Activate"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* ===== COPY CODE DIALOG ===== */}
          <Dialog
            open={copyDialog.open}
            onClose={handleCopyClose}
            PaperProps={{ sx: { borderRadius: 3, maxWidth: 400 } }}
          >
            <DialogTitle sx={{ pb: 1 }}>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: "info.main" }}>
                  <CheckCircleIcon />
                </Avatar>
                <Typography variant="h6">Code Copied!</Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                <AlertTitle>Success</AlertTitle>
                Offer code <strong>{copyDialog.code}</strong> has been copied to
                clipboard.
              </Alert>
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button onClick={handleCopyClose} variant="contained" fullWidth>
                OK
              </Button>
            </DialogActions>
          </Dialog>

          {/* ===== SNACKBAR ===== */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          >
            <Alert
              onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
              severity={snackbar.severity}
              variant="filled"
              sx={{ width: "100%", borderRadius: 2 }}
              iconMapping={{
                success: <CheckCircleIcon fontSize="inherit" />,
                error: <ErrorIcon fontSize="inherit" />,
                warning: <WarningIcon fontSize="inherit" />,
                info: <InfoIcon fontSize="inherit" />,
              }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default Offers;